import React, { useState, useEffect } from 'react';
import { useColorTheme } from '../../context/ColorThemeContext';

const FunctionalAlertModal = ({ 
  isOpen, 
  onClose, 
  title = "Action Required",
  message, 
  type = "warning", // warning, error, info, success
  icon = null,
  actionButton = null,
  secondaryButton = null
}) => {
  const { colorTheme } = useColorTheme();
  const [stage, setStage] = useState('entering'); // entering, showing, exiting

  useEffect(() => {
    if (isOpen) {
      setStage('entering');
      
      // Animation sequence
      const timer1 = setTimeout(() => setStage('showing'), 300);
      
      return () => {
        clearTimeout(timer1);
      };
    }
  }, [isOpen]);

  const handleClose = () => {
    setStage('exiting');
    setTimeout(() => {
      onClose?.();
    }, 300);
  };

  const getTypeStyles = (alertType) => {
    const styles = {
      warning: {
        gradient: 'from-yellow-400 via-orange-500 to-red-500',
        bgGlow: 'from-yellow-50 via-orange-50 to-red-50 dark:from-yellow-900/20 dark:via-orange-900/20 dark:to-red-900/20',
        border: 'border-yellow-300 dark:border-yellow-600',
        iconBg: 'from-yellow-400 via-orange-500 to-red-500',
        defaultIcon: '⚠️'
      },
      error: {
        gradient: 'from-red-400 via-red-500 to-red-600',
        bgGlow: 'from-red-50 via-red-50 to-red-100 dark:from-red-900/20 dark:via-red-900/20 dark:to-red-800/20',
        border: 'border-red-300 dark:border-red-600',
        iconBg: 'from-red-400 via-red-500 to-red-600',
        defaultIcon: '❌'
      },
      info: {
        gradient: 'from-blue-400 via-blue-500 to-blue-600',
        bgGlow: 'from-blue-50 via-blue-50 to-blue-100 dark:from-blue-900/20 dark:via-blue-900/20 dark:to-blue-800/20',
        border: 'border-blue-300 dark:border-blue-600',
        iconBg: 'from-blue-400 via-blue-500 to-blue-600',
        defaultIcon: 'ℹ️'
      },
      success: {
        gradient: colorTheme === 'purple' ? 'from-purple-400 via-purple-500 to-purple-600' : 'from-green-400 via-green-500 to-green-600',
        bgGlow: colorTheme === 'purple' ? 'from-purple-50 via-purple-50 to-purple-100 dark:from-purple-900/20 dark:via-purple-900/20 dark:to-purple-800/20' : 'from-green-50 via-green-50 to-green-100 dark:from-green-900/20 dark:via-green-900/20 dark:to-green-800/20',
        border: colorTheme === 'purple' ? 'border-purple-300 dark:border-purple-600' : 'border-green-300 dark:border-green-600',
        iconBg: colorTheme === 'purple' ? 'from-purple-400 via-purple-500 to-purple-600' : 'from-green-400 via-green-500 to-green-600',
        defaultIcon: '✅'
      }
    };
    return styles[alertType] || styles.warning;
  };

  const typeStyle = getTypeStyles(type);
  const displayIcon = icon || typeStyle.defaultIcon;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      {/* Main Alert Card */}
      <div className={`relative transform transition-all duration-300 ease-out ${
        stage === 'entering' ? 'scale-95 opacity-0 rotate-1' :
        stage === 'showing' ? 'scale-100 opacity-100 rotate-0' :
        'scale-95 opacity-0 -rotate-1'
      }`}>
        <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md mx-4 border-3 ${typeStyle.border} relative overflow-hidden`}>
          {/* Background glow effect */}
          <div className={`absolute inset-0 bg-gradient-to-br ${typeStyle.bgGlow} rounded-2xl`}></div>
          
          {/* Header */}
          <div className="relative text-center mb-6">
            <div className="inline-flex items-center space-x-3 mb-4">
              <h2 className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${typeStyle.gradient}`}>
                {title}
              </h2>
            </div>
          </div>

          {/* Icon */}
          <div className="relative flex justify-center mb-6">
            <div className={`relative w-20 h-20 rounded-full flex items-center justify-center text-4xl bg-gradient-to-br ${typeStyle.iconBg} text-white shadow-2xl ${
              stage === 'showing' ? 'animate-pulse' : ''
            }`}>
              {displayIcon}
              
              {/* Glow effects */}
              <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${typeStyle.iconBg} opacity-40 ${
                stage === 'showing' ? 'animate-ping' : ''
              }`}></div>
              
              {/* Ring effect */}
              <div className={`absolute -inset-3 rounded-full border-2 ${typeStyle.border} ${
                stage === 'showing' ? 'animate-pulse' : ''
              }`}></div>
            </div>
          </div>

          {/* Message */}
          <div className="relative text-center mb-8">
            <div className={`bg-gradient-to-r ${typeStyle.bgGlow} rounded-xl p-4 border-2 ${typeStyle.border}`}>
              <p className="text-base font-medium text-gray-700 dark:text-gray-300 leading-relaxed">
                {message}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="relative space-y-3">
            {actionButton && (
              <div className="w-full">
                {actionButton}
              </div>
            )}
            
            <div className={`flex ${actionButton && secondaryButton ? 'space-x-3' : 'justify-center'}`}>
              {secondaryButton && (
                <div className="flex-1">
                  {secondaryButton}
                </div>
              )}
              
              {!actionButton && (
                <button
                  onClick={handleClose}
                  className={`px-8 py-3 bg-gradient-to-r ${typeStyle.gradient} text-white rounded-xl font-bold text-lg hover:shadow-lg transition-all duration-300 transform hover:scale-105`}
                >
                  Got it! 👍
                </button>
              )}
              
              {actionButton && (
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-500 transition-all duration-200"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>

          {/* Decorative Elements */}
          {stage === 'showing' && (
            <div className="absolute inset-0 pointer-events-none">
              {/* Corner sparkles */}
              <div className="absolute top-4 right-4 text-yellow-400 text-xl animate-pulse">✨</div>
              <div className="absolute bottom-4 left-4 text-yellow-400 text-xl animate-pulse" style={{ animationDelay: '0.5s' }}>✨</div>
              
              {type === 'warning' && (
                <>
                  <div className="absolute top-8 left-8 text-orange-400 text-lg animate-bounce" style={{ animationDelay: '0.2s' }}>⚡</div>
                  <div className="absolute bottom-8 right-8 text-red-400 text-lg animate-bounce" style={{ animationDelay: '0.7s' }}>⚡</div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FunctionalAlertModal;